import React from 'react';
import MoksyLogger from './utils/logger';

export default class ErrorBoundary extends React.Component {
  state = { hasError: false, errorId: null, message: '', stack: '', copied: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    const errorId = `ERR-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;

    /**
     * Keep the cause, and show it.
     *
     * This screen said "The issue has been logged automatically" beside a
     * reference generated with Math.random() and stored nowhere. MoksyLogger
     * writes to the console and only in development, so on production nothing
     * was logged, the reference matched no record, and the person looking at
     * the crash had strictly less information than the browser already had.
     *
     * Every UI crash therefore cost a round trip: someone had to be asked to
     * open devtools and paste the real error. The boundary had it all along.
     */
    this.setState({
      errorId,
      message: String(error?.message || error || 'Unknown error'),
      // First few frames only: enough to name the component and line, not a
      // wall of framework internals.
      stack: String(error?.stack || '').split('\n').slice(0, 4).join('\n'),
      componentStack: String(info?.componentStack || '').split('\n').slice(0, 4).join('\n')
    });

    MoksyLogger.error('UI_CRASH', error, { info, errorId });
  }

  #details() {
    const { errorId, message, stack, componentStack } = this.state;
    return [
      `Reference: ${errorId}`,
      `Page: ${window.location.pathname}${window.location.search}`,
      `Error: ${message}`,
      stack ? `\n${stack}` : '',
      componentStack ? `\nComponent:${componentStack}` : ''
    ].filter(Boolean).join('\n');
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
          <div className="max-w-md w-full text-center space-y-6">
            {/* Code */}
            <p className="text-[120px] font-black leading-none text-gray-100 select-none">500</p>

            {/* Heading */}
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-gray-800">Something went wrong</h1>
              <p className="text-gray-500 text-sm">
                Moksy encountered an unexpected error.
              </p>
            </div>

            {/* What actually broke. Shown rather than hidden behind a
                reference that resolves to nothing. */}
            {this.state.errorId && (
              <div className="bg-background border border-gray-200 rounded-xl px-4 py-3 font-mono text-xs text-left space-y-2">
                <div className="text-gray-500">
                  Reference: <span className="text-primary font-semibold">{this.state.errorId}</span>
                </div>
                {this.state.message && (
                  <div className="text-red-600 break-words">{this.state.message}</div>
                )}
                {this.state.stack && (
                  <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-words text-[10px] leading-4 text-gray-400">
                    {this.state.stack}
                  </pre>
                )}
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(this.#details());
                      this.setState({ copied: true });
                      setTimeout(() => this.setState({ copied: false }), 1500);
                    } catch { /* clipboard unavailable */ }
                  }}
                  className="rounded-md border border-gray-200 px-2 py-1 text-[10px] font-medium text-gray-500 hover:text-gray-700"
                >
                  {this.state.copied ? 'copied' : 'copy details'}
                </button>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 py-3 bg-primary text-white rounded-xl font-bold text-sm uppercase tracking-wide hover:bg-secondary transition-colors"
              >
                Reload Page
              </button>
              <button
                onClick={() => { window.location.href = '/'; }}
                className="flex-1 py-3 border border-gray-200 text-gray-600 rounded-xl font-semibold text-sm hover:bg-gray-50 transition-colors"
              >
                Go to Home
              </button>
            </div>

            {/* Branding */}
            <p className="text-[11px] text-gray-300 lowercase">
              moksy<span className="text-primary">.</span>
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}